import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { red, grey } from "@mui/material/colors";
import { notification } from "../../../utils/Notifications";
import {
  createWarehouseByAdmin,
  createWarehouseBySuperadmin,
  getUsers,
} from "../../../api/userApi";
import useLogout from "../../../hooks/auth/authHook.js";
import {
  Button,
  Container,
  Dialog,
  FormControl,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

AgencyFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  zones: PropTypes.array,
};

export default function AgencyFormDialog({
  onClose,
  open,
  fetchUsers,
  zones = [],
}) {
  const [logout] = useLogout();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;
  const [name, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [addressAlias, setAddressAlias] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [state, setState] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [street, setStreet] = useState("");
  const [outdoorNumber, setOutdoorNumber] = useState("");
  const [interiorNumber, setInteriorNumber] = useState("");
  const [addressReferences, setAddressReferences] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleNameChange = (event) => setName(event.target.value);
  const handleMiddleNameChange = (event) => setMiddleName(event.target.value);
  const handleLastNameChange = (event) => setLastName(event.target.value);
  const handlePhoneChange = (event) => {
    setPhoneNumber(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: !validatePhoneNumber(event.target.value)
        ? "El teléfono debe tener 10 números"
        : "",
    }));
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      email: !validateEmail(event.target.value)
        ? "Correo electrónico no válido"
        : "",
    }));
  };
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleAddressAliasChange = (event) =>
    setAddressAlias(event.target.value);
  const handleZipCodeChange = (event) => {
    setZipCode(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      zipCode: !validateZipCode(event.target.value)
        ? "El código postal debe tener 5 números"
        : "",
    }));
  };
  const handleStateChange = (event) => setState(event.target.value);
  const handleSuburbChange = (event) => setSuburb(event.target.value);
  const handleCityChange = (event) => setCity(event.target.value);
  const handleStreetChange = (event) => setStreet(event.target.value);
  const handleOutdoorNumberChange = (event) =>
    setOutdoorNumber(event.target.value);
  const handleInteriorNumberChange = (event) =>
    setInteriorNumber(event.target.value);
  const handleAddressReferencesChange = (event) =>
    setAddressReferences(event.target.value);
  const handleZoneChange = (event) => setSelectedZone(event.target.value);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const regex = /^\d{10}$/;
    return regex.test(phoneNumber);
  };

  const validateZipCode = (zipCode) => {
    const regex = /^\d{5}$/;
    return regex.test(zipCode);
  };

  useEffect(() => {
    const validateForm = () => {
      const basicFieldsValid =
        name &&
        middleName &&
        lastName &&
        phoneNumber &&
        email &&
        password &&
        zipCode &&
        city &&
        suburb &&
        street &&
        outdoorNumber &&
        addressAlias;
      const zoneValid = isSuperAdmin ? selectedZone : true;
      const hasNoErrors =
        !errors.email && !errors.phoneNumber && !errors.zipCode;
      setIsFormValid(basicFieldsValid && zoneValid && hasNoErrors);
    };

    validateForm();
  }, [
    name,
    middleName,
    lastName,
    phoneNumber,
    email,
    password,
    zipCode,
    city,
    suburb,
    street,
    outdoorNumber,
    addressAlias,
    selectedZone,
    isSuperAdmin,
    errors,
  ]);

  const buttonStyles = {
    width: "120px",
    borderRadius: "0.5rem",
    height: "38px",
  };

  const activeButtonStyles = {
    ...buttonStyles,
    backgroundColor: red["A400"],
    color: "white",
    "&:hover": {
      backgroundColor: red["A700"],
    },
  };

  const inactiveButtonStyles = {
    ...buttonStyles,
    backgroundColor: grey[500],
    color: grey[300],
  };

  const clearFields = () => {
    setName("");
    setMiddleName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setPassword("");
    setAddressAlias("");
    setZipCode("");
    setState("");
    setSuburb("");
    setCity("");
    setStreet("");
    setOutdoorNumber("");
    setInteriorNumber("");
    setAddressReferences("");
    setSelectedZone("");
    setErrors({});
  };

  const handleCreateAgencyClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    const warehouseData = {
      name,
      middleName,
      lastName,
      phoneNumber,
      email,
      password,
      addressAlias,
      zipCode,
      state,
      suburb,
      city,
      street,
      outdoorNumber,
      interiorNumber,
      addressReferences,
    };

    if (isSuperAdmin) {
      const selectedZoneObj = zones.find((zone) => {
        const zoneId =
          typeof zone === "string"
            ? zone
            : zone.id_zone || zone.id || zone.zone_code;
        return (
          zoneId === selectedZone || String(zoneId) === String(selectedZone)
        );
      });
      const zoneId = selectedZoneObj
        ? typeof selectedZoneObj === "string"
          ? selectedZoneObj
          : selectedZoneObj.id_zone || selectedZoneObj.id
        : selectedZone;
      warehouseData.available_zones = zoneId ? [Number(zoneId)] : [];
    }

    const createFunction = isSuperAdmin
      ? createWarehouseBySuperadmin
      : createWarehouseByAdmin;

    createFunction(warehouseData)
      .then((response) => {
        if (
          response.data.status_Code === 200 &&
          response.data.status_Message === "exist email"
        ) {
          notification({ text: "El correo ya esta en uso", type: "error" });
          setIsLoading(false);
          return;
        }

        if (response.data.status_Code === 201) {
          notification({ text: "Sucursal creada", type: "success" });
          getUsers()
            .then((response) => {
              if (response.data.status_Code === 200) {
                fetchUsers();
                onClose();
              } else if (response.data.status_Code === 401) {
                notification({
                  text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
                  type: "error",
                });
                logout();
                onClose();
              } else {
                notification({
                  text: "Error al obtener los usuarios",
                  type: "error",
                });
                onClose();
              }
            })
            .catch(() => {
              notification({
                text: "Error de conexión, revisa tu conexión a internet",
                type: "error",
              });
              onClose();
            })
            .finally(() => {
              clearFields();
              setIsLoading(false);
            });
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al crear la sucursal",
            type: "error",
          });
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
        clearFields();
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  useEffect(() => {
    if (!open) {
      clearFields();
    }
  }, [open]);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{ style: { borderRadius: "16px" } }}
    >
      <Container style={{ padding: 36 }}>
        <Typography
          component="div"
          variant="h5"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            color: grey[800],
            marginBottom: 26,
          }}
        >
          Nueva sucursal
        </Typography>

        <FormControl variant="standard">
          <Container
            sx={{
              padding: "12px 0px 8px 0px !important",
              width: "100%",
              display: "flex",
              justifyContent: "start",
            }}
          >
            <Typography
              component="div"
              variant="body1"
              style={{ fontWeight: "bold", color: grey[800] }}
            >
              Información personal
            </Typography>
          </Container>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="name"
                label="* Nombre"
                variant="standard"
                value={name}
                onChange={handleNameChange}
                style={{ width: "100%" }}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="middleName"
                label="* Apellido paterno"
                variant="standard"
                value={middleName}
                onChange={handleMiddleNameChange}
                style={{ width: "100%" }}
                error={Boolean(errors.middleName)}
                helperText={errors.middleName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="lastName"
                label="* Apellido materno"
                variant="standard"
                value={lastName}
                onChange={handleLastNameChange}
                style={{ width: "100%" }}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="phoneNumber"
                label="* Teléfono"
                variant="standard"
                value={phoneNumber}
                onChange={handlePhoneChange}
                style={{ width: "100%" }}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="email"
                label="* Correo electrónico"
                variant="standard"
                value={email}
                onChange={handleEmailChange}
                style={{ width: "100%" }}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={isSuperAdmin ? 6 : 12}
              md={isSuperAdmin ? 6 : 12}
            >
              <TextField
                id="password"
                label="* Contraseña"
                variant="standard"
                value={password}
                onChange={handlePasswordChange}
                style={{ width: "100%" }}
                error={Boolean(errors.password)}
                helperText={errors.password}
              />
            </Grid>
            {isSuperAdmin && (
              <Grid item xs={12} sm={6} md={6}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="zone-select-label">* Zona</InputLabel>
                  <Select
                    labelId="zone-select-label"
                    id="zone-select"
                    value={selectedZone}
                    onChange={handleZoneChange}
                  >
                    {zones.map((zone) => {
                      const zoneId =
                        typeof zone === "string"
                          ? zone
                          : zone.id_zone || zone.id || zone.zone_code;
                      const zoneName =
                        typeof zone === "string"
                          ? zone
                          : zone.zone_name ||
                            zone.name ||
                            zone.zone_code ||
                            zone.id;
                      return (
                        <MenuItem key={zoneId} value={zoneId}>
                          {zoneName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Container
            sx={{
              padding: "42px 0px 8px 0px !important",
              width: "100%",
              display: "flex",
              justifyContent: "start",
            }}
          >
            <Typography
              component="div"
              variant="body1"
              style={{
                fontWeight: "bold",
                color: grey[800],
                paddingLeft: "0px",
              }}
            >
              Información de la dirección
            </Typography>
          </Container>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                id="addressAlias"
                label="* Alias del domicilio"
                variant="standard"
                value={addressAlias}
                onChange={handleAddressAliasChange}
                style={{ width: "100%" }}
                error={Boolean(errors.addressAlias)}
                helperText={errors.addressAlias}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                id="suburb"
                label="* Colonia"
                variant="standard"
                value={suburb}
                onChange={handleSuburbChange}
                style={{ width: "100%" }}
                error={Boolean(errors.suburb)}
                helperText={errors.suburb}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="street"
                label="* Calle"
                variant="standard"
                value={street}
                onChange={handleStreetChange}
                style={{ width: "100%" }}
                error={Boolean(errors.street)}
                helperText={errors.street}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="outdoorNumber"
                label="* Número exterior"
                variant="standard"
                value={outdoorNumber}
                onChange={handleOutdoorNumberChange}
                style={{ width: "100%" }}
                error={Boolean(errors.outdoorNumber)}
                helperText={errors.outdoorNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="interiorNumber"
                label="Número interior"
                variant="standard"
                value={interiorNumber}
                onChange={handleInteriorNumberChange}
                style={{ width: "100%" }}
                error={Boolean(errors.interiorNumber)}
                helperText={errors.interiorNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="zipCode"
                label="* Código postal"
                variant="standard"
                value={zipCode}
                onChange={handleZipCodeChange}
                style={{ width: "100%" }}
                error={Boolean(errors.zipCode)}
                helperText={errors.zipCode}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="city"
                label="* Ciudad"
                variant="standard"
                value={city}
                onChange={handleCityChange}
                style={{ width: "100%" }}
                error={Boolean(errors.city)}
                helperText={errors.city}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="state"
                label="* Estado"
                variant="standard"
                value={state}
                onChange={handleStateChange}
                style={{ width: "100%" }}
                error={Boolean(errors.state)}
                helperText={errors.state}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                id="addressReferences"
                label="Referencias del domicilio"
                variant="standard"
                value={addressReferences}
                onChange={handleAddressReferencesChange}
                style={{ width: "100%" }}
                error={Boolean(errors.addressReferences)}
                helperText={errors.addressReferences}
              />
            </Grid>
          </Grid>
        </FormControl>

        <Container
          style={{
            display: "flex",
            justifyContent: "end",
            width: "100%",
            padding: 0,
            marginTop: 36,
            gap: 12,
          }}
        >
          <Button
            variant="standard"
            color="primary"
            onClick={() => {
              onClose();
            }}
            style={{
              width: "120px",
              borderRadius: "0.5rem",
              height: "38px",
              color: red["A400"],
              backgroundColor: red["50"],
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid || isLoading}
            onClick={handleCreateAgencyClick}
            sx={isFormValid ? activeButtonStyles : inactiveButtonStyles}
          >
            {isLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Crear"
            )}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
